import { useAgentMutationsAll } from "@/agents/agent-mutations"
import { useAgentsListAll } from "@/agents/catalog"
import { usePersonasList } from "@/persona"
import { useTemplatesList } from "@/templates/catalog"
import { useTestSetsList } from "@/test-set/catalog"

import { GROUP_BY_TYPE, type GroupByType } from "../../types/chart-types"
import { getMetadataGroupDisplayName, parseMetadataGroupKey } from "./splitKeys"

export interface DisplayNameLookups {
  agents: Map<string, string>
  mutations: Map<string, string>
  personas: Map<string, string>
  templates: Map<string, string>
  testSets: Map<string, string>
}

export function useDisplayNameLookups(): {
  lookups: DisplayNameLookups | null
  isLoading: boolean
  error: Error | null
} {
  const agentsQuery = useAgentsListAll()
  const personasQuery = usePersonasList()
  const templatesQuery = useTemplatesList()
  const testSetsQuery = useTestSetsList()
  const mutationsQuery = useAgentMutationsAll()

  const isLoading =
    agentsQuery.isLoading ||
    personasQuery.isLoading ||
    templatesQuery.isLoading ||
    testSetsQuery.isLoading ||
    mutationsQuery.isLoading
  const error =
    agentsQuery.error ||
    personasQuery.error ||
    templatesQuery.error ||
    testSetsQuery.error ||
    mutationsQuery.error

  const lookups: DisplayNameLookups | null = (() => {
    if (
      !agentsQuery.data ||
      !personasQuery.data ||
      !templatesQuery.data ||
      !testSetsQuery.data ||
      !mutationsQuery.data ||
      isLoading
    ) {
      return null
    }

    const agents = new Map<string, string>()
    const mutations = new Map<string, string>()
    const personas = new Map<string, string>()
    const templates = new Map<string, string>()
    const testSets = new Map<string, string>()

    agentsQuery.data.forEach((agent) => {
      if (agent.display_name) {
        if (agent.id) {
          agents.set(agent.id, agent.display_name)
        }
        if (agent.customer_agent_id) {
          agents.set(agent.customer_agent_id, agent.display_name)
        }
      }
    })

    personasQuery.data.forEach((persona) => {
      if (persona.id && persona.display_name) {
        personas.set(persona.id, persona.display_name)
      }
    })

    templatesQuery.data.forEach((template) => {
      if (template.value && template.label) {
        templates.set(template.value, template.label)
      }
    })

    testSetsQuery.data.forEach((testSet) => {
      if (testSet.id && testSet.display_name) {
        testSets.set(testSet.id, testSet.display_name)
      }
    })

    const mutationsData = mutationsQuery.data ?? []
    mutationsData.forEach((mutation) => {
      if (mutation.id && mutation.display_name) {
        mutations.set(mutation.id, mutation.display_name)
      }
    })

    return { agents, mutations, personas, templates, testSets }
  })()

  return {
    lookups,
    isLoading,
    error: (error as Error | null) || null,
  }
}

export function getDisplayName(
  id: string,
  dimension: GroupByType,
  lookups: DisplayNameLookups | null,
): string {
  if (!lookups || !id) {
    return id || "Unknown"
  }

  switch (dimension) {
    case GROUP_BY_TYPE.AGENT:
      return lookups.agents.get(id) || id
    case GROUP_BY_TYPE.MUTATION: {
      const mutationName = lookups.mutations.get(id)
      if (mutationName) {
        return mutationName
      }
      // Fallback: COALESCE may have returned an agent_id for base (no mutation) rows
      const agentName = lookups.agents.get(id)
      if (agentName) {
        return `${agentName} (Base)`
      }
      return id
    }
    case GROUP_BY_TYPE.PERSONA:
      return lookups.personas.get(id) || id
    case GROUP_BY_TYPE.TEMPLATE:
      return lookups.templates.get(id) || id
    case GROUP_BY_TYPE.TEST_SET:
      return lookups.testSets.get(id) || id
    case "assignee":
    default:
      return id
  }
}

export function mergeAgentNameLookups(
  lookups: DisplayNameLookups | null,
  agentNames: Record<string, string> | undefined,
): DisplayNameLookups | null {
  if (!agentNames || Object.keys(agentNames).length === 0) {
    return lookups
  }

  const agents = new Map(lookups?.agents ?? [])
  Object.entries(agentNames).forEach(([agentId, agentName]) => {
    agents.set(agentId, agentName)
  })

  return {
    agents,
    mutations: lookups?.mutations ?? new Map<string, string>(),
    personas: lookups?.personas ?? new Map<string, string>(),
    templates: lookups?.templates ?? new Map<string, string>(),
    testSets: lookups?.testSets ?? new Map<string, string>(),
  }
}

export function splitKeyToDisplayName(
  key: string,
  dimension: GroupByType | undefined,
  lookups: DisplayNameLookups | null | undefined,
): string {
  if (!key || key === "Unknown") {
    return "Unknown"
  }

  if (key.includes("\u001E")) {
    const [groupId, stringValue] = key.split("\u001E")
    const metadataGroup = parseMetadataGroupKey(groupId)

    if (dimension && lookups) {
      const displayName = getDisplayName(groupId, dimension, lookups)
      const dimensionLabel = getDimensionLabel(dimension)
      return `${stringValue} (${dimensionLabel}: ${displayName})`
    }

    return `${stringValue} (${metadataGroup ? getMetadataGroupDisplayName(groupId) : groupId})`
  }

  if (dimension && lookups) {
    return getDisplayName(key, dimension, lookups)
  }

  return getMetadataGroupDisplayName(key)
}

/**
 * Convert dimension key to human-readable label
 */
function getDimensionLabel(dimension: GroupByType): string {
  switch (dimension) {
    case GROUP_BY_TYPE.AGENT:
      return "Agent"
    case GROUP_BY_TYPE.MUTATION:
      return "Mutation"
    case GROUP_BY_TYPE.PERSONA:
      return "Persona"
    case GROUP_BY_TYPE.TEMPLATE:
      return "Template"
    case GROUP_BY_TYPE.TEST_SET:
      return "Test Set"
    case "assignee":
      return "Assignee"
    default:
      return "Group"
  }
}

export function createDisplayNameResolver(
  dimension: GroupByType | undefined,
  lookups: DisplayNameLookups | null | undefined,
): ((raw: string) => string) | undefined {
  if (!dimension || !lookups) {
    return undefined
  }
  return (raw: string) => getDisplayName(raw, dimension, lookups)
}
